/**
 * @class
 * Example use:
 * 	var resizeDebouncer = new Debouncer(callbackFn);
 *  window.addEventListener('resize', resizeDebouncer.run, false);
 *
 * Or, if using JQuery (but you can also
 * 	var resizeDebouncer = new Debouncer(callbackFn);
 *  $(window).bind('resize', resizeDebouncer.run);
 *
 * Or, all on one line:
 *  $(window).bind('resize', (new Debouncer(callbackFn)).run);
 *  
 */
import {AbstractCancellable} from "./Cancellable";
import CancellableQueue from "./CancellableQueue";

export enum Mode {
	leadingEdge,
	trailingEdge,
	both
}

interface DebouncerArgs<T extends any[]> {
	readonly callback: (this: any, ...args:T) => void,
	readonly mode?: Mode,
	readonly timeout?: number,
	readonly queue?: CancellableQueue,
	readonly onCancelled?: (debouncer: Debouncer<T>, callbackArgs?: T) => void
}
export default class Debouncer<T extends any[]> extends AbstractCancellable {
	private readonly callback: (this: any, ...args: T) => void;
	private readonly mode: Mode;
	private readonly timeout?: number;
	private readonly onCancelled?: (debouncer: Debouncer<T>, callbackArgs?: T) => void;

	private waitingID: number | undefined;
	private additionalCallAfterLeadingEdge: boolean | undefined;

	private callbackArgs: T | undefined;

	/**
	 * @param {function} callback
	 * @param {boolean} [mode=Mode.trailingEdge] - If `mode` is set to true, the callback function is called on the leading edge and at the end of the interval
	 *                                                  if an event happens since first event but not during waiting period. If false, called at the end of each interval
	 *                                                  and not at the start.
	 * @param {number} [timeout] - The interval in ms to use to debounce. If not specified, the debouncer uses requestAnimationFrame.
	 * @param {CancellableQueue} [queue] - The queue to which this debouncer should be attached
	 * @param {function} onCancelled - a callback for when the Debouncer is cancelled
	 */
	constructor(
		{callback, mode = Mode.trailingEdge, timeout, queue, onCancelled}: DebouncerArgs<T>
	) {
		super(queue);

		this.callback = callback;
		this.mode = mode;
		this.timeout = timeout;
		this.onCancelled = onCancelled;
	}

	public readonly run = (...args:T) => {
		if (this.isActive()) {
			if (this.mode !== Mode.trailingEdge) {
				if (this.waitingID === undefined) {
					// Call now (if mode is not set to trailingEdit).
					this.callback.apply(this, args);
				} else if (this.mode !== Mode.leadingEdge) {
					this.callbackArgs = args;
					this.additionalCallAfterLeadingEdge = true;
				}
			} else {
				this.callbackArgs = args;
			}

			if (this.waitingID === undefined) {
				if (this.timeout === undefined) {
					this.waitingID = window.requestAnimationFrame(this.laterFn);
				} else {
					this.waitingID = window.setTimeout(this.laterFn, this.timeout);
				}
			}
		}
	}

	private readonly laterFn = () => {
		this.waitingID = undefined;
		if (this.mode === Mode.trailingEdge || this.additionalCallAfterLeadingEdge) {
			this.additionalCallAfterLeadingEdge = false;
			this.callback.apply(this, this.callbackArgs!); // this.callbackArgs will always be defined here
			this.callbackArgs = undefined;
		}
	}

	/**
	 * Run the callback method immediately without debouncing.
	 * Any active debouncing is canceled first.
	 * @public
	 */
	public runNow(...args: T) {
		if (this.isActive()) {
			this.cancelQueued();
			this.callback.apply(this, args);
		}
	}

	/**
	 * Run the callback method immediately without debouncing ignoring whether the Debouncer has been cancelled.
	 * Any active debouncing is canceled first.
	 * @public
	 */
	public forceRunNow(...args: T) {
		this.cancelQueued();
		this.callback.apply(this, args);
	}

	protected doCancel = () => {
		this.cancelQueued();
		if (this.onCancelled !== undefined) {
			this.onCancelled(this, this.callbackArgs);
		}
	}

	/**
	 * Cancel a call to 'run'.
	 * @public
	 */
	public cancelQueued() {
		if (this.waitingID !== undefined) {
			if (this.timeout === undefined) {
				window.cancelAnimationFrame(this.waitingID);
			} else {
				window.clearTimeout(this.waitingID);
			}

			this.waitingID = undefined;
		}
	}
}
